import { createContext, useContext, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";

import { useAuth } from "../hooks";
import { authSession } from "../lib/auth-session";
import API, { appServer } from "~/api";

interface AuthState {
  booting: boolean;
}

const AppBootContext = createContext<AuthState | null>(null);

export function AppBootProvider({ children }: { children: ReactNode }) {
  const [booting, setBooting] = useState(true);
  const initializationDidRun = useRef(false);
  const { clearAuth } = useAuth();

  useEffect(() => {
    const onErrorSubscription = appServer.addOnAPIErrorListener((error) => {
      if (error.response?.status === 401) {
        clearAuth();
      }
    });

    const initializeApplication = async () => {
      initializationDidRun.current = true;

      const authRes = await API.auth.refreshWeb();
      if (authRes.ok) {
        authSession.restore(authRes.data.token);
      } else {
        clearAuth();
      }
      setBooting(false);
    };

    if (!initializationDidRun.current) {
      void initializeApplication();
    }

    return () => {
      onErrorSubscription.remove();
    };
  }, [clearAuth]);

  return (
    <AppBootContext.Provider value={{ booting }}>
      {children}
    </AppBootContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAppBoot(): AuthState {
  const context = useContext(AppBootContext);
  if (!context) {
    throw new Error("useAppBoot must be used within AppBootProvider");
  }
  return context;
}
